import { useT } from "../../../shared/i18n/i18n";
import { History } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../../shared/lib/cn";
import type { ReplayMoment, ReplayMomentKind, ReplayStep } from "../model/use-sprint-replay";

const toneDotClasses: Record<ReplayMoment["tone"], string> = {
  neutral: "bg-muted-foreground/45",
  positive: "bg-emerald-500",
  warning: "bg-amber-500",
};

type LogFilter = "all" | "done" | "risks" | "scope";

const filterKinds: Record<Exclude<LogFilter, "all">, ReplayMomentKind[]> = {
  done: ["completed"],
  risks: ["reopened", "stalled"],
  scope: ["carried", "removed", "scope-added"],
};

const filterLabels: Array<{ key: LogFilter; labelKey: string }> = [
  { key: "all", labelKey: "sprint.all" },
  { key: "scope", labelKey: "sprint.scope" },
  { key: "done", labelKey: "sprint.done" },
  { key: "risks", labelKey: "sprint.risks" },
];

function formatDay(at: number) {
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", weekday: "short" }).format(new Date(at));
}

function formatTime(at: number) {
  const date = new Date(at);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

// The sprint's full story as a scannable list: every moment, filterable by
// what a PM digs for (scope changes, completions, risks). Clicking a row
// rewinds the board to that moment; the row at the playhead stays in view.
export function SprintReplayLog({
  activeAt,
  onJump,
  steps,
}: {
  activeAt: number | null;
  onJump: (at: number) => void;
  steps: ReplayStep[];
}) {
  const t = useT();
  const [filter, setFilter] = useState<LogFilter>("all");
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLButtonElement | null>(null);

  const moments = useMemo(() => steps.flatMap((step) => step.moments), [steps]);
  const counts = useMemo(() => {
    const byFilter = { all: moments.length, done: 0, risks: 0, scope: 0 };
    for (const moment of moments) {
      for (const key of ["done", "risks", "scope"] as const) {
        if (filterKinds[key].includes(moment.kind)) byFilter[key] += 1;
      }
    }
    return byFilter;
  }, [moments]);

  const visible = useMemo(
    () => (filter === "all" ? moments : moments.filter((moment) => filterKinds[filter].includes(moment.kind))),
    [filter, moments],
  );
  const activeIndex = useMemo(() => {
    if (activeAt === null) return -1;
    let index = -1;
    for (const [position, moment] of visible.entries()) {
      if (moment.at <= activeAt) index = position;
      else break;
    }
    return index;
  }, [activeAt, visible]);

  const days = useMemo(() => {
    const grouped: Array<{ day: string; moments: Array<{ index: number; moment: ReplayMoment }> }> = [];
    for (const [index, moment] of visible.entries()) {
      const day = formatDay(moment.at);
      const current = grouped[grouped.length - 1];
      if (current && current.day === day) current.moments.push({ index, moment });
      else grouped.push({ day, moments: [{ index, moment }] });
    }
    return grouped;
  }, [visible]);

  // Keep the playhead row visible by scrolling the list itself —
  // scrollIntoView would also drag every scrollable ancestor (the page).
  useEffect(() => {
    const list = listRef.current;
    const row = activeRowRef.current;
    if (!list || !row) return;
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.top < listRect.top) list.scrollTop += rowRect.top - listRect.top;
    else if (rowRect.bottom > listRect.bottom) list.scrollTop += rowRect.bottom - listRect.bottom;
  }, [activeIndex]);

  // Absolutely positioned so the panel takes the row's height instead of
  // dictating it — otherwise a long history would stretch the whole page and
  // the internal list scroll would never engage.
  return (
    <div className="absolute inset-y-0 right-0 hidden w-72 flex-col rounded-lg bg-muted/30 lg:flex">
      <div className="flex items-center gap-1.5 px-3 pb-1.5 pt-2.5">
        <History className="size-3.5 text-muted-foreground" />
        <span className="text-xs font-medium">{t("sprint.replayHistory")}</span>
        <span className="ml-auto text-[11px] tabular-nums text-muted-foreground/70">{visible.length}</span>
      </div>
      <div className="flex items-center gap-0.5 self-start rounded-md bg-muted/50 p-0.5 mx-3 mb-1.5">
        {filterLabels.map((entry) => (
          <button
            className={cn(
              "rounded px-1.5 py-0.5 text-[11px] transition-colors",
              filter === entry.key ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
              entry.key !== "all" && counts[entry.key] === 0 && "opacity-40",
            )}
            disabled={entry.key !== "all" && counts[entry.key] === 0}
            key={entry.key}
            onClick={() => setFilter(entry.key)}
            type="button"
          >
            {t(entry.labelKey)}
            {entry.key === "all" ? "" : ` ${counts[entry.key]}`}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-1.5" ref={listRef}>
        {days.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">{t("sprint.nothingHere")}</p>
        ) : null}
        {days.map((group) => (
          <div key={group.day}>
            <p className="px-2 pb-0.5 pt-2 text-[11px] font-medium text-muted-foreground/70">{group.day}</p>
            {group.moments.map(({ index, moment }) => (
              <button
                className={cn(
                  "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/60",
                  index === activeIndex && "bg-muted/70",
                )}
                key={`${moment.at}:${index}`}
                onClick={() => onJump(moment.at)}
                ref={index === activeIndex ? activeRowRef : undefined}
                type="button"
              >
                <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", toneDotClasses[moment.tone])} />
                <span className="min-w-0 flex-1 text-xs leading-snug text-foreground/85">{moment.message}</span>
                <span className="shrink-0 pt-px text-[11px] tabular-nums text-muted-foreground/70">{formatTime(moment.at)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
